/**
 * The commit pre-flight — BR-COMMIT-4, FR-ENTRY-10c, AC-37.
 *
 * The last screen before the morning's numbers become the club's record, so it
 * is the one place in the app that is deliberately unhurried. Everything the
 * spec asks to be shown is shown: the game count, per-player counts, who was
 * present and played nothing, the change log, anything unresolved, and results
 * that arrived after a previous commit.
 *
 * Two rules shape it:
 *
 *   Nothing is committed without an explicit confirmation (BR-COMMIT-4), and
 *   never automatically (BR-COMMIT-8). The button says what it will do.
 *
 *   A blocker is shown with the way to clear it, not just the fact of it
 *   (COMMIT-03). Duplicates and conflicts are resolved from here.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type Preflight } from "../api.js";
import { ResolveConflict } from "./ResolveConflict.js";
import { ResolveDuplicate } from "./ResolveDuplicate.js";
import { AuditLog } from "./AuditLog.js";
import { ShareSummary } from "./ShareSummary.js";

export function CommitScreen({
  sessionId,
  unsyncedOps,
  nameOf,
  isAdmin,
  onClose,
  onCommitted,
}: {
  sessionId: string;
  unsyncedOps: number;
  nameOf: (id: string) => string;
  isAdmin: boolean;
  onClose: () => void;
  onCommitted: () => Promise<void>;
}): React.JSX.Element {
  const [report, setReport] = useState<Preflight | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport(await api.preflight(sessionId, unsyncedOps));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the summary.");
    }
  }, [sessionId, unsyncedOps]);

  /**
   * BR-COMMIT-7 / K-1 — incorporate or reject, then reload.
   *
   * `onCommitted` rather than a local mutation: incorporating a game changes
   * the standings, the award and the summary, and every one of those is
   * derived. Re-reading is the only way for this screen and the session behind
   * it to agree.
   */
  const decideLateArrival = async (
    gameId: string,
    decision: "incorporate" | "reject",
    reason?: string,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.lateArrival(gameId, { decision, reason });
      await load();
      await onCommitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record that decision.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  if (sharing) {
    return <ShareSummary sessionId={sessionId} onClose={() => setSharing(false)} />;
  }

  if (showHistory) {
    return <AuditLog sessionId={sessionId} onClose={() => setShowHistory(false)} />;
  }

  if (!report) {
    return (
      <main className="centre">{error ? <p className="error">{error}</p> : "Checking…"}</main>
    );
  }

  // COMMIT-03 — one blocker at a time, with the way to clear it. Showing four
  // resolution screens at once is how people tap the wrong one.
  const duplicate = report.duplicates[0];
  const conflict = report.conflicts[0];

  async function commit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.commit(sessionId, {
        unsyncedOps,
        overrideReason: overrideReason.trim() || undefined,
      });
      await onCommitted();
      if (!result.changed) setError("This session was already committed.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not commit.");
      await load();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <main className="session">
      <header className="session-head">
        <strong>Finish the session</strong>
        <button className="link" onClick={onClose}>
          Back
        </button>
      </header>

      {report.status === "committed" ? (
        <p className="sync-warning">
          This session is committed. The numbers are in the club&apos;s record.
        </p>
      ) : null}

      {/* FR-STAT-12 — one tap to the shareable summary, offered whether or not
          the session is committed; the summary says which it is. */}
      {report.gameCount > 0 ? (
        <button className="primary" onClick={() => setSharing(true)}>
          Share the summary
        </button>
      ) : null}

      {duplicate ? (
        <ResolveDuplicate
          candidate={duplicate}
          nameOf={nameOf}
          onResolved={load}
          onSkip={() => setReport({ ...report, duplicates: report.duplicates.slice(1) })}
        />
      ) : conflict ? (
        <ResolveConflict
          conflict={conflict}
          onResolved={load}
          onSkip={() => setReport({ ...report, conflicts: report.conflicts.slice(1) })}
        />
      ) : null}

      <section>
        <h2>{report.gameCount} games</h2>
        <ul className="queue">
          {report.played.map((p) => (
            <li key={p.playerId}>
              <span>
                {p.displayName}
                {p.isGuest ? <span className="guest-flag">guest</span> : null}
              </span>
              <span className="muted small">
                {p.gamesPlayed} {p.gamesPlayed === 1 ? "game" : "games"}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* AC-37 — listed separately, and explicitly without consequence. */}
      {report.presentButDidNotPlay.length > 0 ? (
        <section>
          <h2>Here but didn&apos;t play</h2>
          <ul className="queue">
            {report.presentButDidNotPlay.map((p) => (
              <li key={p.playerId}>
                <span>{p.displayName}</span>
                <span className="muted small">no games</span>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Nothing happens to them for this — it is here in case somebody was
            marked present by mistake.
          </p>
        </section>
      ) : null}

      {/*
        K-1 — a result that reached the server after the session was committed.
        It is held out of every statistic until somebody decides, and this is
        where the deciding happens.

        It said so and offered nothing. The endpoint has existed since the
        commit work and `api.lateArrival` has existed in the client, and
        neither had a caller: the screen named a problem an admin could see and
        could not act on. Third endpoint in this project found with no caller,
        and the reason the club could not resolve a real one.
      */}
      {report.lateArrivals.length > 0 ? (
        <section>
          <h2>Arrived after the session was finished</h2>
          {report.lateArrivals.map((late) => (
            <div key={late.gameId} className="block">
              <p>
                A result from {late.enteredByName ?? "another phone"} reached the
                server after this session was finished, so it is being held out
                of the statistics.
              </p>
              {isAdmin ? (
                <div className="row" style={{ marginTop: ".6rem" }}>
                  <button
                    className="chip chip-on"
                    disabled={busy}
                    onClick={() => {
                      void decideLateArrival(late.gameId, "incorporate");
                    }}
                  >
                    Count it
                  </button>
                  <button
                    className="chip chip-warn"
                    disabled={busy}
                    onClick={() => {
                      const reason = window.prompt(
                        "Reject this result?\n\nIt stays in the record, marked rejected. Say why:",
                        "Entered against the wrong session",
                      );
                      if (!reason) return;
                      void decideLateArrival(late.gameId, "reject", reason);
                    }}
                  >
                    Reject it
                  </button>
                </div>
              ) : (
                <p className="muted small">An admin decides this one.</p>
              )}
            </div>
          ))}
          <p className="muted small">
            Counting it puts the game into the standings and recomputes them.
            Rejecting keeps the record and leaves it out — nothing is erased
            either way.
          </p>
        </section>
      ) : null}

      {/* FR-ENTRY-10c — counts, with the full log available inline. */}
      {report.editedCount + report.deletedCount > 0 ? (
        <section>
          <h2>Changes after entry</h2>
          <p className="muted small">
            {report.editedCount} edited, {report.deletedCount} removed.{" "}
            <button className="link" onClick={() => setShowLog((v) => !v)}>
              {showLog ? "Hide" : "Show"} the log
            </button>{" "}
            <button className="link" onClick={() => setShowHistory(true)}>
              Full history
            </button>
          </p>
          {showLog ? (
            <ul className="queue">
              {report.changeLog.map((entry, i) => (
                <li key={`${entry.gameId ?? "x"}-${String(i)}`}>
                  <span>{describe(entry.action)}</span>
                  <span className="muted small">
                    {entry.actorName ?? "someone"}
                    {entry.classification ? ` · ${entry.classification}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {report.blockers.length > 0 ? (
        <section>
          <h2>Before you can finish</h2>
          {report.blockers.map((blocker) => (
            <p key={blocker.kind} className="sync-warning">
              {blocker.message}
            </p>
          ))}
        </section>
      ) : null}

      {error ? <p className="error">{error}</p> : null}

      {report.status !== "committed" ? (
        <section className="stack">
          {/* An admin may override a blocker, and must say why. Nobody else
              can, and it is never silent. */}
          {report.blockers.length > 0 && isAdmin ? (
            <label>
              Reason for finishing anyway
              <input
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Checked with both players"
              />
            </label>
          ) : null}

          {confirming ? (
            <>
              <p>
                This makes {report.gameCount} games part of the club&apos;s record. Only an
                admin can change them afterwards.
              </p>
              <button className="primary" disabled={busy} onClick={() => void commit()}>
                Yes, finish the session
              </button>
              <button className="link" onClick={() => setConfirming(false)}>
                Not yet
              </button>
            </>
          ) : (
            <button
              className="primary"
              disabled={busy || (report.blockers.length > 0 && !(isAdmin && overrideReason.trim()))}
              onClick={() => setConfirming(true)}
            >
              Finish the session
            </button>
          )}
        </section>
      ) : null}
    </main>
  );
}

/** Audit actions, in words rather than in identifiers. */
function describe(action: string): string {
  switch (action) {
    case "game.edit":
      return "Score corrected";
    case "game.undo":
      return "Game removed";
    case "game.duplicate_removed":
      return "Duplicate removed";
    case "conflict.resolve":
      return "Two versions settled";
    case "sync.late_arrival":
      return "Arrived after commit";
    case "sync.late_arrival_incorporated":
      return "Late result included";
    case "sync.late_arrival_rejected":
      return "Late result rejected";
    default:
      return action;
  }
}
